import { useCallback, useEffect, useState } from "react";

const SITE_KEY = "6LfOL8wUAAAAAKE86ab48OtkoAxROTWwN53x7SUJ";
const RECAPTCHA_URL = `https://www.recaptcha.net/recaptcha/api.js?render=${SITE_KEY}`;
const TOKEN_REFRESH_INTERVAL = 110000;
// If reCAPTCHA hasn't become ready within this window we treat it as failed.
// The script can download but still never initialise when the challenge
// backend (gstatic/google) is unreachable - common on networks that block
// Google, e.g. mainland China - which used to leave the submit button
// disabled forever with no explanation.
const LOAD_TIMEOUT = 8000;

export const useRecaptcha = (action: string) => {
  const [token, setToken] = useState<string | null>(null);
  const [isLoadingToken, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const generateToken = useCallback(async (): Promise<string | null> => {
    if (!window.grecaptcha) {
      return null;
    }

    try {
      const newToken = await window.grecaptcha.execute(SITE_KEY, { action });
      setToken(newToken);
      // Recovered (e.g. a slow network that eventually succeeded).
      if (newToken) setLoadError(false);
      return newToken;
    } catch (error) {
      console.warn("reCAPTCHA token generation failed:", error);
      setToken(null);
      return null;
    }
  }, [action]);

  useEffect(() => {
    let isMounted = true;
    const script = document.createElement("script");
    script.src = RECAPTCHA_URL;
    script.async = true;
    script.defer = true;

    const failTimer = setTimeout(() => {
      if (isMounted) {
        setLoadError(true);
        setIsLoading(false);
      }
    }, LOAD_TIMEOUT);

    const handleLoad = () => {
      window.grecaptcha.ready(async () => {
        if (!isMounted) return;
        const newToken = await generateToken();
        clearTimeout(failTimer);
        setIsLoading(false);
        // Script ran but no token could be minted (challenge backend blocked).
        if (!newToken) setLoadError(true);
      });
    };

    const handleError = () => {
      if (!isMounted) return;
      clearTimeout(failTimer);
      setLoadError(true);
      setIsLoading(false);
    };

    script.addEventListener("load", handleLoad);
    script.addEventListener("error", handleError);
    document.body.appendChild(script);

    return () => {
      isMounted = false;
      clearTimeout(failTimer);
      document.body.removeChild(script);
      script.removeEventListener("load", handleLoad);
      script.removeEventListener("error", handleError);
    };
  }, [generateToken]);

  useEffect(() => {
    const interval = setInterval(generateToken, TOKEN_REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [generateToken]);

  return { token, isLoadingToken, loadError, regenerateToken: generateToken };
};
